import React, { useState } from 'react';
import { TableSection, Pagination } from 'components';
import { useAPI, useDebounce } from 'hooks';
import { getUserName, formatDate } from 'helpers';
import { useQuery } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const AgenciesPage = () => {
  const { api } = useAPI();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearchQuery = useDebounce(search, 1000);
  const itemsPerPage = 30;
  const limit = 30;

  const fetchParams = search.length > 0 ? { q: debouncedSearchQuery } : { limit };

  const { data: agencies } = useQuery({
    queryFn: api.list('agency/', fetchParams),
    queryKey: ['agency/', debouncedSearchQuery, limit],
    keepPreviousData: true,
  });

  const totalPages = agencies ? Math.ceil(agencies.length / itemsPerPage) : 1;
  const startIndex = (page - 1) * itemsPerPage;
  const currentPageData = agencies ? agencies.slice(startIndex, startIndex + itemsPerPage) : [];

  const nextPage = () => setPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setPage((p) => Math.max(p - 1, 1));

  const activationVariant = (active) =>
    active ? 'bg-green-100 text-green-800 border-green-300' : 'bg-red-100 text-red-700 border-red-300';

  const authorizationVariant = (auth) => {
    switch (auth) {
      case 'Accepted':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Requested':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handleGrantAuthorization = () => {};

  return (
    <TableSection
      title="Liste des Agences"
      description="Gérez les agences et leurs informations"
      actionLabel="+ Ajouter une agence"
      onSearch={(value) => setSearch(value.trim())}
      searchPlaceholder="Rechercher une agence..."
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>IFU</TableHead>
            <TableHead>Nom</TableHead>
            <TableHead>Administrateur</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Pèlerins (élligibles / enregistrés)</TableHead>
            <TableHead>Autorisation</TableHead>
            <TableHead>Création</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {currentPageData.map((agency) => (
            <TableRow key={agency.id} className="hover:bg-gray-50">
              <TableCell className="font-medium">{agency.ifu}</TableCell>
              <TableCell>{agency.name}</TableCell>
              <TableCell>
                <div className="flex flex-col text-sm space-y-1">
                  <span className="font-medium">{getUserName(agency.admin.first_name, agency.admin.last_name)}</span>
                  <span className="text-gray-500">{agency.admin.email}</span>
                  <span className="text-gray-500">{agency.phone}</span>
                  <span className="text-gray-500">{agency.address}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={`${activationVariant(agency.admin.is_verified && agency.admin.is_active)} rounded-full`}
                >
                  {agency.admin.is_verified && agency.admin.is_active ? 'Activé' : 'Désactivé'}
                </Badge>
              </TableCell>
              <TableCell className="space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span>{`${agency.count_pilgrim_eligible} / ${agency.count_pilgrim_registered}`}</span>
                </div>
                {/* To be improved */}
                {/* <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${(agency.count_pilgrim_eligible / agency.count_pilgrim_registered) * 100}%` }}
                  />
                </div> */}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={`${authorizationVariant(agency.authorization)} rounded-full`}>
                  {agency.authorization}
                </Badge>
              </TableCell>
              <TableCell>{formatDate(agency.created)}</TableCell>
              <TableCell className="flex gap-2">
                <Button variant="outline" size="sm">
                  Editer
                </Button>
                <Button variant="secondary" size="sm" onClick={() => handleGrantAuthorization(agency.id)}>
                  Octroyer une autorisation
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Pagination page={page} totalPages={totalPages} onPrev={prevPage} onNext={nextPage} />
    </TableSection>
  );
};

export default AgenciesPage;
